'use client';
import type {} from '@mui/material/styles';

import { Controller, useFormContext } from 'react-hook-form';

import FormHelperText from '@mui/material/FormHelperText';

import { CustomSlider, type CustomSliderProps } from '../slider/custom-slider';

// ----------------------------------------------------------------------

type RHFSliderProps<T> = Omit<CustomSliderProps, 'value'> & {
  name: string;
  helperText?: React.ReactNode;
  parser?: (value: T) => number | number[];
  formatter?: (value: number | number[]) => T;
  restrictionLimit?: number;
  showRestrictedArea?: boolean;
  createRestrictedOnChange?: (
    originalOnChange: (value: number | number[]) => void,
  ) => (value: number | number[]) => void;
  triggerVisualFeedback?: boolean;
  onVisualFeedbackComplete?: () => void;
};

export function RHFSlider<T>({
  name,
  helperText,
  formatter,
  parser,
  restrictionLimit,
  showRestrictedArea,
  createRestrictedOnChange,
  triggerVisualFeedback,
  onVisualFeedbackComplete,
  ...other
}: RHFSliderProps<T>) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <>
          <CustomSlider
            {...other}
            value={parser ? parser(field.value) : field.value}
            onChange={
              createRestrictedOnChange
                ? createRestrictedOnChange((val) => {
                    field.onChange(formatter ? formatter(val as number | number[]) : (val as number | number[]));
                  })
                : (val) => {
                    field.onChange(formatter ? formatter(val as number | number[]) : (val as number | number[]));
                  }
            }
            restrictionLimit={restrictionLimit}
            showRestrictedArea={showRestrictedArea}
            triggerVisualFeedback={triggerVisualFeedback}
            onVisualFeedbackComplete={onVisualFeedbackComplete}
          />
          {(!!error || helperText) && <FormHelperText error={!!error}>{error?.message || helperText}</FormHelperText>}
        </>
      )}
    />
  );
}
